import { useMutation, useQuery } from '@tanstack/react-query';
import { getProductiveFacilities } from '../../../../services/productive-facilities/getProductiveFacilities';
import { useState } from 'react';
import { useDisclosure } from '@agrotoken-tech/ui';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import { requestApprovalProductiveFacility } from '../../../../services/productive-facilities/requestApprovalProductiveFacility';
import {
  CommonProductiveAffidavitSerializer,
  CommonProductiveFacilitySerializer,
} from '@bcba/serializers';
import { useBreakpointValue } from '@chakra-ui/react';

const PRODUCTIVE_FACILITIES_PAGES = {
  DETAIL: 'DETAIL',
  UPSERT_FACILITY: 'UPSERT_FACILITY',
  UPSERT_AFFIDAVIT: 'UPSERT_AFFIDAVIT',
} as const;
type ProductiveFacilitiesPage = keyof typeof PRODUCTIVE_FACILITIES_PAGES;

interface Props {
  refetchStatusSummary: () => void;
}

export const useProductiveFacilities = ({ refetchStatusSummary }: Props) => {
  const { t } = useTranslation();
  const { showSuccessToast } = useToastsHook();

  const {
    data,
    isPending,
    isError,
    refetch: refetchProductiveFacilities,
  } = useQuery({
    queryKey: ['productive-facilities'],
    queryFn: getProductiveFacilities,
  });

  const refetch = () => {
    refetchProductiveFacilities();
    refetchStatusSummary();
  };

  const [currentPage, setCurrentPage] = useState<ProductiveFacilitiesPage>(
    PRODUCTIVE_FACILITIES_PAGES.DETAIL,
  );

  const showUpsertFacilityPage =
    currentPage === PRODUCTIVE_FACILITIES_PAGES.UPSERT_FACILITY;
  const showUpsertAffidavitPage =
    currentPage === PRODUCTIVE_FACILITIES_PAGES.UPSERT_AFFIDAVIT;

  const goBack = () => setCurrentPage(PRODUCTIVE_FACILITIES_PAGES.DETAIL);

  const [defaultFacilityIndex, setDefaultFacilityIndex] = useState<number>();
  const [defaultAffidavitIndex, setDefaultAffidavitIndex] = useState<number>();

  const [selectedFacility, setSelectedFacility] =
    useState<CommonProductiveFacilitySerializer>();
  const [selectedAffidavit, setSelectedAffidavit] =
    useState<CommonProductiveAffidavitSerializer>();

  const {
    isOpen: isOpenDeleteFacility,
    onOpen: onOpenDeleteFacility,
    onClose: onCloseDeleteFacility,
  } = useDisclosure();
  const {
    isOpen: isOpenDeleteAffidavit,
    onOpen: onOpenDeleteAffidavit,
    onClose: onCloseDeleteAffidavit,
  } = useDisclosure();

  const onSuccessDeleteFacility = () => {
    setDefaultFacilityIndex(undefined);
    refetch();
    onCloseDeleteFacility();
    showSuccessToast({
      title: t('explorer.productiveFacilities.deleteFacility.success.title'),
    });
  };
  const onSuccessUpsertFacility = () => {
    const isUpdating = Boolean(selectedFacility);
    if (!isUpdating) {
      setDefaultFacilityIndex(data?.length);
    }
    refetch();
    showSuccessToast({
      title: isUpdating
        ? t('explorer.productiveFacilities.updateFacility.success.title')
        : t('explorer.productiveFacilities.createFacility.success.title'),
    });
    goBack();
  };
  const onCancelUpsertFacility = () => {
    goBack();
  };

  const onSuccessDeleteAffidavit = () => {
    setDefaultAffidavitIndex(undefined);
    refetch();
    onCloseDeleteAffidavit();
    showSuccessToast({
      title: t('explorer.productiveFacilities.deleteAffidavit.success.title'),
    });
  };
  const onSuccessUpsertAffidavit = () => {
    const isUpdating = Boolean(selectedAffidavit);
    refetch();
    if (!isUpdating) {
      setDefaultAffidavitIndex(selectedFacility?.productiveAffidavits?.length);
    }
    showSuccessToast({
      title: isUpdating
        ? t('explorer.productiveFacilities.updateAffidavit.success.title')
        : t('explorer.productiveFacilities.createAffidavit.success.title'),
    });
    goBack();
  };
  const onCancelUpsertAffidavit = () => {
    goBack();
  };

  const onAddFacility = () => {
    setSelectedFacility(undefined);
    setCurrentPage(PRODUCTIVE_FACILITIES_PAGES.UPSERT_FACILITY);
  };
  const handleOnEditFacility = (
    facility: CommonProductiveFacilitySerializer,
    index: number,
  ) => {
    setSelectedFacility(facility);
    setCurrentPage(PRODUCTIVE_FACILITIES_PAGES.UPSERT_FACILITY);
    setDefaultFacilityIndex(index);
  };
  const handleOnDeleteFacility = (
    facility: CommonProductiveFacilitySerializer,
  ) => {
    setSelectedFacility(facility);
    onOpenDeleteFacility();
  };

  const handleOnAddAffidavit = (
    facility: CommonProductiveFacilitySerializer,
    facilityIndex: number,
  ) => {
    setCurrentPage(PRODUCTIVE_FACILITIES_PAGES.UPSERT_AFFIDAVIT);
    setSelectedAffidavit(undefined);
    setSelectedFacility(facility);
    setDefaultFacilityIndex(facilityIndex);
  };
  const handleOnEditAffidavit = (
    facility: CommonProductiveFacilitySerializer,
    affidavit: CommonProductiveAffidavitSerializer,
    affidavitIndex: number,
    facilityIndex: number,
  ) => {
    setSelectedAffidavit(affidavit);
    setSelectedFacility(facility);
    setCurrentPage(PRODUCTIVE_FACILITIES_PAGES.UPSERT_AFFIDAVIT);
    setDefaultFacilityIndex(facilityIndex);
    setDefaultAffidavitIndex(affidavitIndex);
  };
  const handleOnDeleteAffidavit = (
    affidavit: CommonProductiveAffidavitSerializer,
  ) => {
    setSelectedAffidavit(affidavit);
    onOpenDeleteAffidavit();
  };

  const { mutate: handleRequestApproval, isPending: isPendingRequestApproval } =
    useMutation({
      mutationFn: requestApprovalProductiveFacility,
      onSuccess: () => {
        refetch();
        showSuccessToast({
          title: t(
            'explorer.productiveFacilities.requestApproval.success.title',
          ),
        });
      },
    });

  const showMobileAccordionItemActions = useBreakpointValue({
    base: true,
    sm: false,
    md: false,
    lg: false,
    xl: false,
  });

  return {
    data,
    isPending,
    isError,
    showUpsertFacilityPage,
    selectedFacility,
    selectedAffidavit,
    showUpsertAffidavitPage,
    onSuccessUpsertFacility,
    onCancelUpsertFacility,
    onSuccessUpsertAffidavit,
    onCancelUpsertAffidavit,
    onAddFacility,
    handleOnEditFacility,
    isOpenDeleteFacility,
    onCloseDeleteFacility,
    handleOnDeleteFacility,
    onSuccessDeleteFacility,
    defaultFacilityIndex,
    handleOnAddAffidavit,
    handleOnEditAffidavit,
    isOpenDeleteAffidavit,
    onCloseDeleteAffidavit,
    handleOnDeleteAffidavit,
    onSuccessDeleteAffidavit,
    defaultAffidavitIndex,
    handleRequestApproval,
    isPendingRequestApproval,
    showMobileAccordionItemActions,
  };
};
